import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import OrganizationOverviewSidebar from './Components/OrganizationOverviewSidebar'
import { makeStyles } from '@material-ui/core/styles'
import { Button, Card, CardActions, CardContent, CardHeader, Grid, TextField, Typography } from '@material-ui/core'
import { FormattedMessage, useIntl } from 'react-intl'
import EditOrganizationInformation from './Components/EditOrgranizationInformation'
import CircularProgress from '@material-ui/core/CircularProgress'
import { organizationActions } from '../../../_state/actions'

const useStyles = makeStyles((theme) => ({
    root: {
        minHeight: '300px',
        marginLeft: 'calc(15% + 25px)',
        marginRight: '25px',
    },
    onboardingTextField: {
        '& .MuiInputBase-inputMultiline': {
            transition: 'max-height 0.3s ease',
            maxHeight: '6em',
            overflow: 'auto',
        },
        '&.expanded .MuiInputBase-inputMultiline': {
            maxHeight: '50vh',
        },
    },
}))

const OrganizationSettings = (props) => {
    const classes = useStyles()
    const intl = useIntl()

    const [currentOrganizationDto, setCurrentOrganizationDto] = useState(null)
    const [tempOnboardingEmailMessage, setTempOnboardingEmailMessage] = useState('')
    const [tempOnboardingEmailSubject, setTempOnboardingEmailSubject] = useState('')
    const [isEmailFieldFocused, setIsEmailFieldFocused] = useState(false)

    useEffect(() => {
        props.dispatch(organizationActions.getMyOrganizations())

        if (props.organization.myOrganizations && props.organization.selectedOrganization) {
            let tempCurrentOrganization = props.organization.myOrganizations.find(
                (x) => x.id === props.organization.selectedOrganization
            )
            if (tempCurrentOrganization) {
                setCurrentOrganizationDto(tempCurrentOrganization)
                setTempOnboardingEmailMessage(tempCurrentOrganization.onboardingEmailMessage || '')
                setTempOnboardingEmailSubject(tempCurrentOrganization.onboardingEmailSubject || '')
            }
        }
    }, [])

    useEffect(() => {
        if (props.organization.myOrganizations && props.organization.selectedOrganization) {
            let tempCurrentOrganization = props.organization.myOrganizations.find(
                (x) => x.id === props.organization.selectedOrganization
            )
            if (tempCurrentOrganization) {
                setCurrentOrganizationDto(tempCurrentOrganization)
                setTempOnboardingEmailMessage(tempCurrentOrganization.onboardingEmailMessage || '')
                setTempOnboardingEmailSubject(tempCurrentOrganization.onboardingEmailSubject || '')
            }
        }
    }, [props.organization.selectedOrganization, props.organization.myOrganizations])

    const handleSaveOnboardingEmailMessage = () => {
        const updateOrganizationInformationDto = {
            organizationId: props.organization.selectedOrganization,
            onboardingEmailMessage: tempOnboardingEmailMessage,
            onboardingEmailSubject: tempOnboardingEmailSubject,
        }
        props.dispatch(
            organizationActions.updateOrgnaizationInfo(
                props.organization.selectedOrganization,
                updateOrganizationInformationDto
            )
        )
    }

    const handleSaveBillingInformation = (
        organizationNumber,
        companyContactEmail,
        companyContactName,
        street,
        countyCode,
        countyName,
        country
    ) => {
        const updateOrganizationInformationDto = {
            organizationId: props.organization.selectedOrganization,
            organizationNumber: organizationNumber,
            contactEmail: companyContactEmail,
            contactPersonName: companyContactName,
            billingAddressStreet: street,
            billingAddressCountyCode: countyCode,
            billingAddressCountyName: countyName,
            billingAddressCountry: country,
        }
        props.dispatch(
            organizationActions.updateOrgnaizationInfo(
                props.organization.selectedOrganization,
                updateOrganizationInformationDto
            )
        )
    }

    return (
        <div className={classes.root}>
            <OrganizationOverviewSidebar path={props.computedMatch?.path} />

            <Grid container spacing={3}>
                {!currentOrganizationDto && (
                    <Grid item xs={12}>
                        <CircularProgress />
                        <Typography variant="body1" style={{ display: 'inline', marginLeft: '10px' }}>
                            <FormattedMessage id="admin.org.loading" defaultMessage="Loading organization..." />
                        </Typography>
                    </Grid>
                )}

                {currentOrganizationDto && (
                    <Grid item xs={12}>
                        <Card style={{ marginTop: '20px' }}>
                            <CardHeader
                                title={
                                    <FormattedMessage
                                        id="admin.edit.org.email.settings"
                                        defaultMessage="Email settings"
                                    />
                                }
                            />
                            <CardContent>
                                <Typography variant="body2" color="textSecondary" style={{ marginBottom: '16px' }}>
                                    <FormattedMessage
                                        id="admin.org.email.settings.description"
                                        defaultMessage="You can optionally customise the onboarding email that new users receive when they are invited to your organization. If set, this message will be included in the invitation email sent to new users."
                                    />
                                </Typography>
                                <TextField
                                    id="onboardingEmailSubject"
                                    label={<FormattedMessage id="admin.org.email.settings.subject.label" defaultMessage="Custom email subject (optional)" />}
                                    value={tempOnboardingEmailSubject}
                                    onChange={(event) => setTempOnboardingEmailSubject(event.target.value)}
                                    margin="normal"
                                    fullWidth
                                    variant="outlined"
                                    placeholder={intl.formatMessage({ id: 'admin.org.email.settings.subject.placeholder', defaultMessage: 'Override the default invitation email subject...' })}
                                />
                                <TextField
                                    id="onboardingEmailMessage"
                                    label={<FormattedMessage id="admin.org.email.settings.label" defaultMessage="Onboarding email message" />}
                                    multiline
                                    minRows={4}
                                    className={`${classes.onboardingTextField}${isEmailFieldFocused ? ' expanded' : ''}`}
                                    value={tempOnboardingEmailMessage}
                                    onChange={(event) => setTempOnboardingEmailMessage(event.target.value)}
                                    onFocus={() => setIsEmailFieldFocused(true)}
                                    onBlur={() => setIsEmailFieldFocused(false)}
                                    margin="normal"
                                    fullWidth
                                    variant="outlined"
                                    placeholder={intl.formatMessage({ id: 'admin.org.email.settings.placeholder', defaultMessage: 'Enter an optional message to include in invitation emails...' })}
                                />
                            </CardContent>
                            <CardActions>
                                <Button
                                    size="large"
                                    onClick={handleSaveOnboardingEmailMessage}
                                    variant="contained"
                                    color="primary"
                                    disabled={
                                        (currentOrganizationDto.onboardingEmailMessage || '') ===
                                            tempOnboardingEmailMessage &&
                                        (currentOrganizationDto.onboardingEmailSubject || '') ===
                                            tempOnboardingEmailSubject
                                    }>
                                    <FormattedMessage id="globalwords.save2" defaultMessage="Save" />
                                </Button>
                            </CardActions>
                        </Card>
                    </Grid>
                )}

                {currentOrganizationDto && (
                    <Grid item xs={12}>
                        <Card style={{ marginTop: '4px' }}>
                            <CardHeader
                                title={
                                    <FormattedMessage
                                        id="admin.edit.org.billing.information"
                                        defaultMessage="Billing information"
                                    />
                                }
                            />
                            <CardContent>
                                <EditOrganizationInformation
                                    organizationDto={currentOrganizationDto}
                                    saveBillingInformation={handleSaveBillingInformation}
                                />
                            </CardContent>
                            <CardActions></CardActions>
                        </Card>
                    </Grid>
                )}
            </Grid>
        </div>
    )
}

function mapStateToProps(state) {
    const { organization, user } = state
    return {
        organization,
        user,
    }
}

const mapDispatchToProps = (dispatch) => {
    return {}
}

export default connect(mapStateToProps, mapDispatchToProps)(OrganizationSettings)
